// Facility Status Mapping Utility
// Maps between user-friendly display names and database values for facility statuses

interface FacilityStatusMapping {
  displayName: string;
  dbValue: string;
  category: string;
  description: string;
}

// Comprehensive facility status mappings
const facilityStatusMappings: FacilityStatusMapping[] = [
  // Active/Operational Statuses
  {
    displayName: "Active",
    dbValue: "active",
    category: "Operational",
    description: "Facility is currently operational",
  },
  {
    displayName: "Opened",
    dbValue: "opened",
    category: "Operational",
    description: "Facility has been opened and is operational",
  },

  // Planning/Development Statuses
  {
    displayName: "Planned",
    dbValue: "planned",
    category: "Development",
    description: "Facility is in planning phase",
  },
  {
    displayName: "Under Construction",
    dbValue: "under_construction",
    category: "Development",
    description: "Facility is currently being built",
  },

  // Expansion Statuses
  {
    displayName: "Expansion Planned",
    dbValue: "expansion_planned",
    category: "Expansion",
    description: "Facility expansion is planned",
  },
  {
    displayName: "Expanding",
    dbValue: "expanding",
    category: "Expansion",
    description: "Facility is currently being expanded",
  },

  // Reduction Statuses
  {
    displayName: "Capacity Reduction Planned",
    dbValue: "capacity_reduction_planned",
    category: "Reduction",
    description: "Facility capacity reduction is planned",
  },
  {
    displayName: "Capacity Reduction",
    dbValue: "capacity_reduction",
    category: "Reduction",
    description: "Facility capacity is being reduced",
  },

  // Inactive/Closed Statuses
  {
    displayName: "Paused",
    dbValue: "paused",
    category: "Inactive",
    description: "Facility operations are temporarily paused",
  },
  {
    displayName: "Closed",
    dbValue: "closed",
    category: "Inactive",
    description: "Facility has been permanently closed",
  },
  {
    displayName: "Closure Planned",
    dbValue: "closure_planned",
    category: "Inactive",
    description: "Facility closure is planned",
  },
  {
    displayName: "Cancelled",
    dbValue: "cancelled",
    category: "Inactive",
    description: "Facility project has been cancelled",
  },
  {
    displayName: "Demolished",
    dbValue: "demolished",
    category: "Inactive",
    description: "Facility has been demolished",
  },

  // Risk Statuses
  {
    displayName: "At Risk",
    dbValue: "at_risk",
    category: "Risk",
    description: "Facility is at risk of closure or issues",
  },

  // Unknown Status
  {
    displayName: "Unknown",
    dbValue: "unknown",
    category: "Unknown",
    description: "Facility status is unknown",
  },
];

// Create lookup maps for efficient conversion
const displayNameToDbValue = new Map<string, string>();
const dbValueToDisplayName = new Map<string, string>();
const categoryMap = new Map<string, FacilityStatusMapping[]>();

// Initialize lookup maps
facilityStatusMappings.forEach((mapping) => {
  displayNameToDbValue.set(mapping.displayName, mapping.dbValue);
  dbValueToDisplayName.set(mapping.dbValue, mapping.displayName);

  if (!categoryMap.has(mapping.category)) {
    categoryMap.set(mapping.category, []);
  }
  categoryMap.get(mapping.category)!.push(mapping);
});

// Export functions for converting between display names and database values

/**
 * Get all facility status display names
 */
export const getAllFacilityStatusDisplayNames = (): string[] => {
  return facilityStatusMappings.map((mapping) => mapping.displayName);
};

/**
 * Get all facility status database values
 */
export const getAllFacilityStatusDbValues = (): string[] => {
  return facilityStatusMappings.map((mapping) => mapping.dbValue);
};

/**
 * Convert display name to database value
 */
export const getDbValueFromDisplayName = (displayName: string): string => {
  const dbValue = displayNameToDbValue.get(displayName);
  if (!dbValue) {
    throw new Error(`Unknown facility status display name: ${displayName}`);
  }
  return dbValue;
};

/**
 * Convert database value to display name
 */
export const getDisplayNameFromDbValue = (dbValue: string): string => {
  const displayName = dbValueToDisplayName.get(dbValue);
  if (!displayName) {
    throw new Error(`Unknown facility status database value: ${dbValue}`);
  }
  return displayName;
};

/**
 * Convert array of display names to database values
 */
export const getDbValuesFromDisplayNames = (
  displayNames: string[]
): string[] => {
  return displayNames.map((name) => getDbValueFromDisplayName(name));
};

/**
 * Convert array of database values to display names
 */
export const getDisplayNamesFromDbValues = (dbValues: string[]): string[] => {
  return dbValues.map((value) => getDisplayNameFromDbValue(value));
};

/**
 * Get facility status mappings by category
 */
export const getFacilityStatusesByCategory = (
  category: string
): FacilityStatusMapping[] => {
  return categoryMap.get(category) || [];
};

/**
 * Get all categories
 */
export const getFacilityStatusCategories = (): string[] => {
  return Array.from(categoryMap.keys());
};

/**
 * Get facility status mapping by display name
 */
export const getFacilityStatusMapping = (
  displayName: string
): FacilityStatusMapping | undefined => {
  return facilityStatusMappings.find(
    (mapping) => mapping.displayName === displayName
  );
};

/**
 * Check if a facility status matches selected statuses
 */
export const facilityMatchesStatuses = (
  facilityStatuses: string[],
  selectedDisplayNames: string[]
): boolean => {
  if (selectedDisplayNames.length === 0) return true;

  const selectedDbValues = getDbValuesFromDisplayNames(selectedDisplayNames);
  return facilityStatuses.some((status) => selectedDbValues.includes(status));
};

/**
 * Get category for a database value
 */
export const getCategoryForDbValue = (dbValue: string): string | undefined => {
  const mapping = facilityStatusMappings.find((m) => m.dbValue === dbValue);
  return mapping?.category;
};

/**
 * Get description for a database value
 */
export const getDescriptionForDbValue = (
  dbValue: string
): string | undefined => {
  const mapping = facilityStatusMappings.find((m) => m.dbValue === dbValue);
  return mapping?.description;
};
